'use client';
import { LocaleMenu } from './GNB/LocaleMenu';
import { PathName } from '@/modules/PathName';
import { Locales, useLocale, useTranslate } from 'app/[locale]/hooks/useTranslate';
import clsx from 'clsx';
import { ArrowDown, CallaboPopover } from 'libs/callabo-ui/common/Popover/CallaboPopover';
import Image from 'next/image';
import Link from 'next/link';
import { HomePaths, Paths, PathsForExternal } from 'src/modules/Paths';
import { Product } from './GNB/Product';
import { usePathname, useSearchParams } from 'next/navigation';
import { setCookie } from '@/utils/cookie/BrowserCookie';
import { useEffect, useRef, useState } from 'react';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';

export function GNB(): JSX.Element {
  const { t } = useTranslate();
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleChangeLanguage = async (newLocale: Locales) => {
    if (locale === newLocale) return;
    setCookie('locale', newLocale, 30 * 24 * 60 * 60); // 쿠키 만료일: 30일
  };

  const [expandAccordion, setExpandAccordion] = useState<boolean>(true);
  const [showBorder, setShowBorder] = useState(false);
  const updating = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!updating.current) {
        updating.current = true;
        requestAnimationFrame(() => {
          const { top } = document.documentElement.getBoundingClientRect();
          setShowBorder(top < 0);
          updating.current = false;
        });
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* 영역 확보용 */}
      <div className="h-20 " />
      <div
        className={clsx(
          'fixed inset-0 z-50 bg-white h-20 transition-all duration-300 border-b border-solid border-gray-fog',
          showBorder ? 'border-opacity-100' : 'border-opacity-0 hover:border-opacity-100'
        )}
      >
        <nav className="h-full p-2 md:px-4 lg:px-10 flex items-center justify-between max-w-homepage *:whitespace-nowrap">
          {/* Desktop GNB */}
          <div className="hidden items-center justify-between w-full md:flex">
            <div className="flex items-center gap-10">
              <Link href={new PathName(Paths.Index).toString()} passHref className="shrink-0">
                <button className="flex items-center">
                  <Image
                    src={'/callabo/homepage/logo_icon.svg'}
                    width={32}
                    height={32}
                    className="shrink-0"
                    alt="c"
                  />
                  <Image
                    src={'/callabo/homepage/logo_name.svg'}
                    className="shrink-0"
                    width={132}
                    height={48}
                    alt="CALLABO"
                  />
                </button>
              </Link>
              <ul className="w-full flex gap-5 lg:gap-8 items-center font-semibold text-black">
                <CallaboPopover
                  options={{
                    anchor: 'none',
                    backdrop: {
                      className:
                        'bg-gnb-backdrop translate-y-20 object-cover bg-no-repeat bg-cover opacity-20 animate-gnbBackDrop',
                    },
                    panel: {
                      className: 'fixed translate-y-4 rounded-xl overflow-hidden',
                    },
                  }}
                >
                  <button className="flex gap-1">
                    <span className="">{t.home.GNB_PRODUCT}</span>
                    <ArrowDown />
                  </button>
                  {({ close }) => (
                    <div className="p-8 space-y-6 md:space-y-5 bg-white flex flex-col items-start">
                      <Product closePopover={close} />
                    </div>
                  )}
                </CallaboPopover>
                <Link className="block" href={HomePaths.Pricing} passHref>
                  <li
                    onMouseDown={() =>
                      analyticsInstance.logEvent('click_gnb_menu', {
                        menu: 'pricing',
                      })
                    }
                    onClick={() => close()}
                    dangerouslySetInnerHTML={{ __html: t.home.GNB_PRICING }}
                  />
                </Link>
                <Link
                  href={PathsForExternal.UseCase}
                  target="_blank"
                  className="flex items-center gap-1"
                >
                  <li
                    onMouseDown={() =>
                      analyticsInstance.logEvent('click_gnb_menu', {
                        menu: 'introduction_case',
                      })
                    }
                    dangerouslySetInnerHTML={{ __html: t.home.GNB_INTRODUCTION_CASE }}
                  />
                  <Image
                    src="/callabo/homepage/arrow_redirect.svg"
                    width={16}
                    height={16}
                    alt=""
                    className="shrink-0"
                  />
                </Link>
                <Link
                  href={PathsForExternal.Blog}
                  target="_blank"
                  className="flex items-center gap-1"
                >
                  <li
                    onMouseDown={() =>
                      analyticsInstance.logEvent('click_gnb_menu', {
                        menu: 'blog',
                      })
                    }
                    dangerouslySetInnerHTML={{ __html: t.common.BLOG }}
                  />
                  <Image
                    src="/callabo/homepage/arrow_redirect.svg"
                    width={16}
                    height={16}
                    alt=""
                    className="shrink-0"
                  />
                </Link>
              </ul>
            </div>
            <div className="flex items-center">
              <Link href={Paths.Login} passHref>
                <button
                  onMouseDown={() => {
                    analyticsInstance.logEvent('click_gnb_menu', {
                      menu: 'login',
                    });
                  }}
                  dangerouslySetInnerHTML={{ __html: t.home.GNB_LOGIN }}
                  className="text-sm md:text-base text-black-light font-semibold mr-4 lg:mr-8"
                />
              </Link>
              <Link href={Paths.Login} passHref>
                <button
                  onMouseDown={() => {
                    analyticsInstance.logEvent('click_try_free_trial', {
                      page: '/',
                      location: 'gnb',
                    });
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t.home.GNB_TRIAL_FREE,
                  }}
                  className={clsx(
                    ' px-4 py-3 border rounded font-semibold box-border transition-all duration-200 ease-in-out border-solid hover:shadow-pale-sm mr-3.5',
                    'text-navy border-blue-fog hover:bg-primary hover:text-white hover:border-primary'
                  )}
                />
              </Link>
              <LocaleMenu />
            </div>
          </div>
          {/* Mobile GNB */}
          <div className="flex items-center justify-between w-full md:hidden px-5">
            <Link href={new PathName(Paths.Index).toString()} passHref className="shrink-0">
              <button className="flex items-center">
                <Image
                  src={'/callabo/homepage/logo_name.svg'}
                  className="shrink-0"
                  width={108}
                  height={40}
                  alt="CALLABO"
                />
              </button>
            </Link>
            <div className="flex items-center justify-end gap-3.5">
              <Link href={Paths.Login} passHref>
                <button
                  onMouseDown={() => {
                    analyticsInstance.logEvent('click_gnb_menu', {
                      menu: 'login',
                    });
                  }}
                  dangerouslySetInnerHTML={{ __html: t.home.GNB_LOGIN }}
                  className="text-black-light font-semibold text-sm"
                />
              </Link>
              <CallaboPopover
                options={{
                  anchor: 'none',
                  backdrop: {
                    className:
                      'bg-gnb-backdrop translate-y-20 object-cover bg-no-repeat bg-cover opacity-20 animate-gnbBackDrop',
                  },
                  panel: {
                    className:
                      'fixed left-1/2 -translate-x-1/2 translate-y-24 top-0 w-[calc(100%_-_32px)] rounded-xl overflow-hidden',
                  },
                }}
              >
                <button>
                  <Image src="/callabo/homepage/icon_menus.svg" width={32} height={32} alt="" />
                </button>
                {({ close }) => (
                  <>
                    <div className="py-4 px-6 space-y-3.5 bg-white flex flex-col items-start w-full">
                      <div className="w-full mt-3.5">
                        <button
                          className="flex items-center justify-between w-full mb-3.5"
                          onClick={() => setExpandAccordion((expandAccordion) => !expandAccordion)}
                        >
                          <p className="text-black-light font-semibold">{t.home.GNB_PRODUCT}</p>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            className={clsx(
                              'shrink-0 transition-[transform] duration-100 ease-in-out opacity-50',
                              expandAccordion ? 'rotate-180' : 'rotate-0'
                            )}
                          >
                            <path
                              d="M15 7.5L10 12.5L5 7.5"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div
                          className={clsx(
                            'space-y-6 divide-y divide-gray-fog flex flex-col items-start overflow-hidden transition-[max-height,padding] duration-400 ease-in-out w-full',
                            expandAccordion ? 'max-h-52' : 'max-h-0'
                          )}
                        >
                          <Product closePopover={close} />
                        </div>
                      </div>
                      <div className="w-full h-px bg-gray-fog"></div>
                      <Link
                        href={HomePaths.Pricing}
                        className="flex items-center justify-between w-full"
                      >
                        <button
                          onMouseDown={() => {
                            analyticsInstance.logEvent('click_gnb_menu', {
                              menu: 'pricing',
                            });
                          }}
                          onClick={() => close()}
                          className="text-black-light font-semibold"
                        >
                          {t.home.GNB_PRICING}
                        </button>
                      </Link>
                      <div className="w-full h-px bg-gray-fog"></div>
                      <Link
                        href={PathsForExternal.UseCase}
                        className="flex items-center justify-between w-full"
                      >
                        <button
                          onMouseDown={() => {
                            analyticsInstance.logEvent('click_gnb_menu', {
                              menu: 'introduction_case',
                            });
                          }}
                          className="text-black-light font-semibold"
                        >
                          {t.home.GNB_INTRODUCTION_CASE}
                        </button>
                        <Image
                          src="/callabo/homepage/arrow_redirect.svg"
                          width={16}
                          height={16}
                          alt=""
                          className="shrink-0"
                        />
                      </Link>
                      <div className="w-full h-px bg-gray-fog"></div>
                      <Link
                        href={PathsForExternal.Blog}
                        className="flex items-center justify-between w-full"
                      >
                        <button
                          onMouseDown={() => {
                            analyticsInstance.logEvent('click_gnb_menu', {
                              menu: 'blog',
                            });
                          }}
                          className="text-black-light font-semibold"
                        >
                          {t.common.BLOG}
                        </button>
                        <Image
                          src="/callabo/homepage/arrow_redirect.svg"
                          width={16}
                          height={16}
                          alt=""
                          className="shrink-0"
                        />
                      </Link>
                    </div>
                    <div className="w-full bg-gray-frost space-y-3.5 border-t border-solid border-gray-fog pt-5 pb-6">
                      <div className="flex items-center justify-center w-full gap-2">
                        <Link href={PathsForExternal.GooglePlayStore} target="_blank" passHref>
                          <button
                            onMouseDown={() => {
                              analyticsInstance.logEvent('click_gnb_menu', {
                                menu: 'google_play_store',
                              });
                            }}
                            className="p-3.5 flex items-center justify-center gap-2 border-gray-fog rounded border border-solid bg-white"
                          >
                            <Image
                              src={'/callabo/homepage/google_play_store.svg'}
                              width={40}
                              height={40}
                              className="w-8 h-8 xl:w-10 xl:h-10 shrink-0"
                              alt="play_store"
                            />
                            <span className="text-black-light text-sm font-semibold">
                              Google Play
                            </span>
                          </button>
                        </Link>
                        <Link href={PathsForExternal.AppleAppStore} target="_blank" passHref>
                          <button
                            onMouseDown={() => {
                              analyticsInstance.logEvent('click_gnb_menu', {
                                menu: 'app_store',
                              });
                            }}
                            className="p-3.5 flex items-center justify-center gap-2 border-gray-fog rounded border border-solid bg-white"
                          >
                            <Image
                              src={'/callabo/homepage/apple_app_store.svg'}
                              width={40}
                              height={40}
                              className="w-8 h-8 xl:w-10 xl:h-10 shrink-0"
                              alt="app_store"
                            />
                            <span className="text-black-light text-sm font-semibold">
                              App Store
                            </span>
                          </button>
                        </Link>
                      </div>
                      <ul
                        className={clsx(
                          'flex items-center justify-center w-full text-gray-ash font-semibold text-sm'
                        )}
                      >
                        <a
                          href={
                            pathname.replace(`/${locale}`, '/ko') +
                            (searchParams.toString() ? `?${searchParams.toString()}` : '')
                          }
                          className="p-2"
                        >
                          <li
                            onClick={() => handleChangeLanguage('ko')}
                            className={clsx(locale === 'ko' && 'text-black-light')}
                          >
                            한국어
                          </li>
                        </a>
                        <a
                          href={
                            pathname.replace(`/${locale}`, '/en') +
                            (searchParams.toString() ? `?${searchParams.toString()}` : '')
                          }
                          className="p-2"
                        >
                          <li
                            onClick={() => handleChangeLanguage('en')}
                            className={clsx(locale === 'en' && 'text-black-light')}
                          >
                            English
                          </li>
                        </a>
                        <a
                          href={
                            pathname.replace(`/${locale}`, '/ja') +
                            (searchParams.toString() ? `?${searchParams.toString()}` : '')
                          }
                          className="p-2"
                        >
                          <li
                            onClick={() => handleChangeLanguage('ja')}
                            className={clsx(locale === 'ja' && 'text-black-light')}
                          >
                            日本語
                          </li>
                        </a>
                      </ul>
                    </div>
                  </>
                )}
              </CallaboPopover>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}
